import { format } from 'date-fns';
import styled from 'styled-components';
import { z } from 'zod';
import { CURRENCY_USD } from 'constants/currency';
import { getCoachPageUrl } from 'constants/pages';
import { EMPTY_COACH_AVATAR_SRC } from 'constants/user';
import {
  FeedbackSentimentEnum,
  FileCategoryEnum,
  FileFormatEnum,
  GetCoachesByGeoQuery,
  GetTournamentsByLocationQuery,
  SportsEnum,
} from 'types/generated/client';
import { VenueAddress, VideoAnalysisSkillsTypeEnum } from 'utils/shared/coachBuilder';
import { addOneHour } from 'utils/shared/date/addHourToSlot';
import { calculateHaversineDistance } from 'utils/shared/geo/calculateHaversineDistance';
import { convertUnitPriceToFormattedPrice } from 'utils/shared/money/convertUnitPriceToFormattedPrice';
import { resolveVenueTimeZone } from 'utils/shared/time/venueDayFilters';
import { getProfileImageUrlOrPlaceholder } from 'utils/shared/user/getProfileImageUrlOrPlaceholder';
import { formatDateRange } from 'components/Explore/Tournaments/utils';
import { CoachType, CombinedCoachType, ImageType, OmittedCoachType, TournamentType } from './props';

export { CUSTOM_COURT_IMG_PLACEHOLDER_SRC } from 'constants/media';

export const getDecoratedTournaments = (
  events: GetTournamentsByLocationQuery['events'],
  centerLatitude: number,
  centerLongitude: number,
): TournamentType[] =>
  events.map((tournament) => {
    return {
      ...tournament,
      startTimestamp: new Date(tournament.startDateTime).getTime(),
      displayDate: formatDateRange(
        new Date(tournament.startDateTime),
        new Date(tournament.endDateTime),
        resolveVenueTimeZone(tournament),
      ),
      distance: Math.round(
        calculateHaversineDistance({
          coord1: {
            latitude: tournament.latitude,
            longitude: tournament.longitude,
          },
          coord2: {
            latitude: centerLatitude,
            longitude: centerLongitude,
          },
          unit: 'miles',
        }),
      ),
    };
  });

export const GradientBackground = styled.div<{ left: string }>`
  border-radius: 9999rem;
  position: absolute;
  height: 100%;
  width: auto;
  max-width: 100vw;
  left: ${({ left }) => left};
  transform: translateX(-50%);
  aspect-ratio: 1/1;
  background: radial-gradient(50% 50% at 50% 50%, #dfecff 0%, rgba(242, 242, 242, 0) 100%);
  z-index: -1;
`;

export const isCoachProfile = (coach: any): coach is GetCoachesByGeoQuery['userProfiles'][0] => {
  return !!coach.username;
};

interface FormatCoachParams {
  coaches: CoachType[];
  centerLatitude: number;
  centerLongitude: number;
  isPreventSorting?: boolean;
}

export const formatCoaches = ({
  coaches,
  centerLatitude,
  centerLongitude,
  isPreventSorting,
}: FormatCoachParams) => {
  const formattedCoaches = coaches.map((coach) => {
    const currency = isCoachProfile(coach)
      ? coach.stripeMerchantCurrency || coach.currencyCoachDefault || CURRENCY_USD
      : CURRENCY_USD;
    const onCourtPrice = convertUnitPriceToFormattedPrice(
      isCoachProfile(coach) ? coach.priceUnitAmountCoachDefault || 0 : 0,
      currency,
    ).priceDisplay;

    const remotePrice = convertUnitPriceToFormattedPrice(
      isCoachProfile(coach) ? coach.priceUnitAmountRemoteCoachDefault || 0 : 0,
      currency,
    ).priceDisplay;

    const href = getCoachPageUrl(isCoachProfile(coach) ? coach.username : coach.id);
    const imageSrc = isCoachProfile(coach)
      ? getProfileImageUrlOrPlaceholder({
          path: coach.profileImagePath || '',
          placeholder: EMPTY_COACH_AVATAR_SRC,
        })
      : coach.profileImageUrl ||
        coach.aboutPhotoImageUrl ||
        coach.logoImageUrl ||
        EMPTY_COACH_AVATAR_SRC;

    const distance =
      coach.geometry?.coordinates?.[1] && coach.geometry?.coordinates?.[0]
        ? calculateHaversineDistance({
            coord1: {
              latitude: centerLatitude || 0,
              longitude: centerLongitude || 0,
            },
            coord2: {
              latitude: coach.geometry.coordinates[1],
              longitude: coach.geometry.coordinates[0],
            },
            unit: 'miles',
          })
        : 0;

    return {
      ...coach,
      fullName: isCoachProfile(coach) ? coach.fullName : '',
      onCourtPrice,
      remotePrice,
      href,
      imageSrc,
      distance,
    };
  });

  // Sort by distance
  return isPreventSorting
    ? formattedCoaches
    : formattedCoaches.sort((a, b) => a.distance - b.distance);
};

export const getLessonSchedularProps = (
  selectedDate: Date,
  activeLocation: VenueAddress,
  activeLesson: CombinedCoachType,
  handleBookAnotherLesson: () => void,
  onPaymentSuccess: () => void,
) => {
  const startTimeString = format(selectedDate, 'hh:mm a');
  const endTimeString = addOneHour(startTimeString);

  return {
    onPaymentSuccess,
    handleBookAnotherLesson,
    fromCalendar: true,
    initialCurrentDateTime: {
      date: selectedDate,
      startTime: startTimeString,
      endTime: endTimeString,
    },
    initialSelectedCourtVenue: {
      id: activeLocation.id,
      title: activeLocation.title || '',
      address: activeLocation.address,
      locationType: activeLocation.locationType,
    },
    coach: activeLesson as never as OmittedCoachType,
    initialBookedLessonId: activeLesson.id,
  };
};

export const videoAnalysisFilesSchema = z.object({
  fileCategory: z.enum([FileCategoryEnum.Coach, FileCategoryEnum.Other, FileCategoryEnum.Profile]),
  fileName: z.string(),
  fileType: z.string(),
  host: z.string(),
  originalFileName: z.string(),
  provider: z.string(),
  providerUrl: z.string(),
  url: z.string(),
  fileFormat: z.enum([FileFormatEnum.Image, FileFormatEnum.Video]),
  providerId: z.string(),
  userId: z.string(),
});

export const lessonAnalysisFormSchema = z.object({
  skills: z.array(z.string()).optional(),
  details: z.string().min(1, { message: 'Details are required.' }),
  videoAnalysisFiles: z.array(videoAnalysisFilesSchema).refine((files) => files.length > 0, {
    message: 'Video is required',
  }),
});

export const lessonAnalysisFormDefaultValues = {
  skills: [],
  details: '',
};

export const getLeadCommunityMailto = () => {
  const email = 'community@bounce.game';
  const subject = 'I lead a community';
  const body = encodeURIComponent(
    'I am interested in leading a community and would like to get more information.',
  );

  return `mailto:${email}?subject=${encodeURIComponent(subject)}&body=${body}`;
};

export const isImageType = (item: any): item is ImageType => {
  return item && typeof item === 'object' && 'src' in item && 'shadow' in item;
};
